<script setup lang="ts">
/**
 * NodeView for the RecordReference node — renders the reference as a record chip
 * inside the editor, reusing RecordElement so it looks identical to record chips
 * everywhere else (type icon + colour + name).
 *
 * The node only stores the junction id + a cached label. A resolver map is
 * provided by DocumentEditor (from the document's mentions links) so the chip
 * shows the live record. Until it resolves (e.g. a just-inserted reference,
 * before the parent refetches), it falls back to the cached label.
 */
import { computed, inject } from "vue"
import { nodeViewProps, NodeViewWrapper } from "@tiptap/vue-3"
import RecordElement from "@auger/ui/components/elements/RecordElement.vue"
import type { useMarkdown } from "@/composables/useMarkdown"

const props = defineProps(nodeViewProps)

const resolverMap = inject<ReturnType<typeof useMarkdown>["referenceMap"] | null>(
  "recordReferenceMap",
  null
)

const record = computed(() => resolverMap?.value?.get(props.node.attrs.linkId) ?? null)
const label = computed(() => props.node.attrs.label || `#${props.node.attrs.linkId}`)
</script>

<template>
  <NodeViewWrapper as="span" class="record-reference-node" :class="{ 'is-selected': selected }">
    <RecordElement v-if="record" :value="record" size="small" readonly fixed />
    <span v-else class="tag link-tag is-small is-link record-chip">{{ label }}</span>
  </NodeViewWrapper>
</template>

<style>
.record-reference-node {
  white-space: nowrap;
  a {
    text-decoration: none;
  }
}
.record-reference-node.is-selected .tag {
  outline: 2px solid var(--auger-primary);
}
</style>
